import React, { Suspense, useEffect } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { TeamsProvider } from './contexts/TeamsContext'
import { LandingPagesProvider } from './contexts/LandingPagesContext'
import { RedirectsProvider } from './contexts/RedirectsContext'
import { PendingLinksCountProvider } from './contexts/PendingLinksCountContext'
import { motion } from 'framer-motion'
import PageMeta from './components/seo/PageMeta'
import { CLIENT_AND_ABOVE_ROLES, MANAGER_LEVEL_ROLES, ADVISOR_LEVEL_ROLES, NEW_ASSOCIATE_ROLES } from './utils/roles'

// Loading component
const LoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <motion.div
      animate={{ rotate: 360 }}
      transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
      className="w-12 h-12 border-4 border-picton-blue border-t-transparent rounded-full"
    />
  </div>
)

// Lazy load pages for better performance
const RecruitingPage = React.lazy(() => import('./pages/public/RecruitingPage'))
const ClientServicesPage = React.lazy(() => import('./pages/public/ClientServicesPage'))
const LoginPage = React.lazy(() => import('./pages/auth/LoginPage'))
const RegisterPage = React.lazy(() => import('./pages/auth/RegisterPage'))
const ConfirmEmailPage = React.lazy(() => import('./pages/auth/ConfirmEmail'))
const ResetPassword = React.lazy(() => import('./pages/Auth/ResetPassword'))
const Dashboard = React.lazy(() => import('./pages/dashboard/Dashboard'))
const NewAssociateDashboard = React.lazy(() => import('./components/dashboard/NewAssociateDashboard'))
const UserProfilePage = React.lazy(() => import('./pages/dashboard/UserProfilePage'))
const ProfilePage = React.lazy(() => import('./pages/dashboard/ProfilePage'))
const AdminPage = React.lazy(() => import('./pages/dashboard/AdminPage'))
const LeadsPage = React.lazy(() => import('./pages/dashboard/LeadsPage'))
const LeadDetailPage = React.lazy(() => import('./pages/dashboard/LeadDetailPage'))
const ClientsPage = React.lazy(() => import('./pages/dashboard/ClientsPage'))
const ClientDetailPage = React.lazy(() => import('./pages/dashboard/ClientDetailPage'))
const BlogDashboardPage = React.lazy(() => import('./pages/dashboard/blog/MyBlogPosts'))
const BlogCreatePostPage = React.lazy(() => import('./pages/dashboard/blog/CreateBlogPost'))
const BlogEditPostPage = React.lazy(() => import('./pages/dashboard/blog/EditBlogPost'))
const TaskManagerDashboard = React.lazy(() => import('./pages/dashboard/tasks/TaskManagerDashboard'))
const CompletedTasksView = React.lazy(() => import('./pages/dashboard/tasks/CompletedTasksView'))
const CategoryView = React.lazy(() => import('./pages/dashboard/tasks/CategoryView'))
const AgentProfilePage = React.lazy(() => import('./pages/public/AgentProfilePage'))
const AgentTrackerLayout = React.lazy(() => import('./pages/AgentTracker/AgentTrackerLayout'))
const AgentTrackerList = React.lazy(() => import('./pages/AgentTracker/AgentTrackerList'))
const AgentTrackerDetail = React.lazy(() => import('./pages/AgentTracker/AgentTrackerDetail'))
const AgentTrackerNew = React.lazy(() => import('./pages/AgentTracker/AgentTrackerNew'))
const PendingLinks = React.lazy(() => import('./pages/AgentTracker/PendingLinks'))
const PendingLinkDetail = React.lazy(() => import('./pages/AgentTracker/PendingLinkDetail'))
const ProtectedRoute = React.lazy(() => import('./components/auth/ProtectedRoute'))
const RedirectsDashboard = React.lazy(() => import('./pages/Admin/Redirects/RedirectsDashboard'))
const LicensedAdvisorsPage = React.lazy(() => import('./pages/Admin/LicensedAdvisorsPage'))
const TeamAdministrationPage = React.lazy(() => import('./pages/Admin/TeamAdministration'))
const OnboardingSettings = React.lazy(() => import('./pages/Admin/OnboardingSettings'))
const RootRedirectHandler = React.lazy(() => import('./pages/public/RootRedirectHandler'))
const RedirectHandler = React.lazy(() => import('./pages/public/RedirectHandler'))
const OnboardingModule = React.lazy(() => import('./pages/Onboarding/OnboardingModule'))
const LandingPagesDashboard = React.lazy(() => import('./pages/LandingPages/LandingPagesDashboard'))
const CreateLandingPage = React.lazy(() => import('./pages/LandingPages/CreateLandingPage'))
const EditLandingPage = React.lazy(() => import('./pages/LandingPages/EditLandingPage'))
const LandingPageAnalytics = React.lazy(() => import('./pages/LandingPages/LandingPageAnalytics'))
const PublicLandingPage = React.lazy(() => import('./pages/LandingPages/PublicLandingPage'))
const ApplicationsPage = React.lazy(() => import('./pages/applications/ApplicationsPage'))
const ApplicationDetailPage = React.lazy(() => import('./pages/applications/ApplicationDetailPage'))
const DocumentRequestsListPage = React.lazy(() => import('./pages/DocumentRequests/DocumentRequestsListPage'))
const CreateDocumentRequestPage = React.lazy(() => import('./pages/DocumentRequests/CreateDocumentRequestPage'))
const DocumentRequestDetailPage = React.lazy(() => import('./pages/DocumentRequests/DocumentRequestDetailPage'))
const PreUnderwritingList = React.lazy(() => import('./pages/PreUnderwriting'))
const PreUnderwritingForm = React.lazy(() => import('./pages/PreUnderwriting/PreUnderwritingForm'))
const PreUnderwritingDetail = React.lazy(() => import('./pages/PreUnderwriting/PreUnderwritingDetail'))
const EventDetailPage = React.lazy(() => import('./pages/Events/EventDetailPage'))
const DocumentPortalPage = React.lazy(() => import('./pages/public/DocumentPortal/DocumentPortalPage'))
const AgentLinksPage = React.lazy(() => import('./pages/AgentLinks/AgentLinks'))
const FinLayout = React.lazy(() => import('./pages/Fin/FinLayout'))
const FinDashboard = React.lazy(() => import('./pages/Fin/src/pages/Dashboard'))
const FinClientManagement = React.lazy(() => import('./pages/Fin/src/pages/ClientManagement'))
const FinClientDetails = React.lazy(() => import('./pages/Fin/src/pages/ClientDetails'))
const FinFinancialAnalysis = React.lazy(() => import('./pages/Fin/src/pages/FinancialAnalysis'))
const FinClientFinancialReport = React.lazy(() => import('./pages/Fin/src/pages/ClientFinancialReport'))
const FinProposalManagement = React.lazy(() => import('./pages/Fin/src/pages/ProposalManagement'))
const FinUserManagement = React.lazy(() => import('./pages/Fin/src/pages/UserManagement'))
const FinClientPortal = React.lazy(() => import('./pages/Fin/src/pages/ClientPortal'))
const FinClientFinancialAnalysis = React.lazy(() => import('./pages/Fin/src/pages/ClientFinancialAnalysis'))
const FinProfileSettings = React.lazy(() => import('./pages/Fin/src/pages/ProfileSettings'))
const FinProjectionsSettings = React.lazy(() => import('./pages/Fin/src/pages/ProjectionsSettings'))
const FinPrivateRoute = React.lazy(() => import('./pages/Fin/src/components/auth/PrivateRoute'))
const PublicFinAnalysis = React.lazy(() => import('./pages/Fin/PublicFinAnalysis'))

const RootRedirect = () => {
  const { user, loading } = useAuth()

  if (loading) {
    return <LoadingFallback />
  }

  return <Navigate to={user ? '/dashboard' : '/login'} replace />
}

import './App.css'

function App() {
  const isDev = Boolean(import.meta.env?.DEV)

  const ADMIN_AND_EMD_ROLES = new Set(['admin', 'emd'])

  useEffect(() => {
    if (!isDev) {
      return
    }

    console.log(`[${new Date().toISOString()}] <App> mounted`)

    return () => {
      console.log(`[${new Date().toISOString()}] <App> unmounted`)
    }
  }, [isDev])

  return (
    <AuthProvider>
      <TeamsProvider>
        <LandingPagesProvider>
          <RedirectsProvider>
            <Router>
              <PendingLinksCountProvider>
                <PageMeta />
                <Suspense fallback={<LoadingFallback />}>
                  <div className="min-h-screen bg-gray-50 overflow-x-hidden max-w-full">
                    <Routes>
                    {/* Public Routes */}
                    <Route path="/" element={<RootRedirect />} />
                    <Route path="/recruiting" element={<RecruitingPage />} />
                    <Route path="/services" element={<ClientServicesPage />} />
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/register" element={<RegisterPage />} />
                    <Route path="/confirm-email" element={<ConfirmEmailPage />} />
                    <Route path="/auth/reset-password" element={<ResetPassword />} />

                    {/* Public FIN Analysis (no login required) */}
                    <Route path="/fin/analysis" element={<PublicFinAnalysis />} />

                    {/* Public Document Portal (no login; client upload by token + code) */}
                    <Route path="/document-portal/:token" element={<DocumentPortalPage />} />

                    <Route
                      path="/admin/redirects"
                      element={
                        <ProtectedRoute>
                          <RedirectsDashboard />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/licensed-advisors"
                      element={
                        <ProtectedRoute allowedRoles={MANAGER_LEVEL_ROLES}>
                          <LicensedAdvisorsPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/team-administration"
                      element={
                        <ProtectedRoute allowedRoles={MANAGER_LEVEL_ROLES}>
                          <TeamAdministrationPage />
                        </ProtectedRoute>
                      }
                    />

                    {/* Protected Dashboard Routes */}
                    <Route
                      path="/dashboard"
                      element={
                        <ProtectedRoute allowedRoles={CLIENT_AND_ABOVE_ROLES}>
                          <Dashboard />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/agent-tracker/*"
                      element={
                        <ProtectedRoute allowedRoles={CLIENT_AND_ABOVE_ROLES}>
                          <AgentTrackerLayout />
                        </ProtectedRoute>
                      }
                    >
                      <Route index element={<AgentTrackerList />} />
                      <Route path="new" element={<AgentTrackerNew />} />
                      <Route
                        path="pending-links"
                        element={
                          <ProtectedRoute allowedRoles={ADMIN_AND_EMD_ROLES}>
                            <PendingLinks />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="pending-links/:linkId"
                        element={
                          <ProtectedRoute allowedRoles={ADMIN_AND_EMD_ROLES}>
                            <PendingLinkDetail />
                          </ProtectedRoute>
                        }
                      />
                      <Route path=":agentId" element={<AgentTrackerDetail />} />
                    </Route>
                    <Route
                      path="/dashboard/new-associate"
                      element={
                        <ProtectedRoute allowedRoles={NEW_ASSOCIATE_ROLES}>
                          <NewAssociateDashboard />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/user-profile"
                      element={
                        <ProtectedRoute>
                          <UserProfilePage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/my-public-page"
                      element={
                        <ProtectedRoute>
                          <ProfilePage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/profile"
                      element={
                        <ProtectedRoute>
                          <Navigate to="/dashboard/my-public-page" replace />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/admin"
                      element={
                        <ProtectedRoute>
                          <AdminPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/admin/onboarding"
                      element={
                        <ProtectedRoute>
                          <OnboardingSettings />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/landing-pages"
                      element={
                        <ProtectedRoute>
                          <LandingPagesDashboard />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/landing-pages/create"
                      element={
                        <ProtectedRoute>
                          <CreateLandingPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/landing-pages/:id/edit"
                      element={
                        <ProtectedRoute>
                          <EditLandingPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/landing-pages/:id/analytics"
                      element={
                        <ProtectedRoute>
                          <LandingPageAnalytics />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/agent-links"
                      element={
                        <ProtectedRoute>
                          <AgentLinksPage />
                        </ProtectedRoute>
                      }
                    />

                    {/* New Leads and Clients Routes */}
                    <Route
                      path="/dashboard/tasks"
                      element={
                        <ProtectedRoute allowedRoles={['emd', 'admin']}>
                          <TaskManagerDashboard />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/tasks/completed"
                      element={
                        <ProtectedRoute allowedRoles={['emd', 'admin']}>
                          <CompletedTasksView />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/tasks/category/:categoryId"
                      element={
                        <ProtectedRoute allowedRoles={['emd', 'admin']}>
                          <CategoryView />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/leads"
                      element={
                        <ProtectedRoute>
                          <LeadsPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/leads/:leadId"
                      element={
                        <ProtectedRoute>
                          <LeadDetailPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/applications"
                      element={
                        <ProtectedRoute allowedRoles={ADVISOR_LEVEL_ROLES}>
                          <ApplicationsPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/applications/:id"
                      element={
                        <ProtectedRoute allowedRoles={ADVISOR_LEVEL_ROLES}>
                          <ApplicationDetailPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/document-requests"
                      element={
                        <ProtectedRoute allowedRoles={ADVISOR_LEVEL_ROLES}>
                          <DocumentRequestsListPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/document-requests/new"
                      element={
                        <ProtectedRoute allowedRoles={ADVISOR_LEVEL_ROLES}>
                          <CreateDocumentRequestPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/document-requests/:id"
                      element={
                        <ProtectedRoute allowedRoles={ADVISOR_LEVEL_ROLES}>
                          <DocumentRequestDetailPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/pre-underwriting"
                      element={
                        <ProtectedRoute allowedRoles={CLIENT_AND_ABOVE_ROLES}>
                          <PreUnderwritingList />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/pre-underwriting/new"
                      element={
                        <ProtectedRoute allowedRoles={CLIENT_AND_ABOVE_ROLES}>
                          <PreUnderwritingForm />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/pre-underwriting/:id"
                      element={
                        <ProtectedRoute allowedRoles={CLIENT_AND_ABOVE_ROLES}>
                          <PreUnderwritingDetail />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/events/:eventId"
                      element={
                        <ProtectedRoute>
                          <EventDetailPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/clients"
                      element={
                        <ProtectedRoute>
                          <ClientsPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/clients/:clientId"
                      element={
                        <ProtectedRoute>
                          <ClientDetailPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/blog"
                      element={
                        <ProtectedRoute>
                          <BlogDashboardPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/blog/new"
                      element={
                        <ProtectedRoute>
                          <BlogCreatePostPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/blog/edit/:id"
                      element={
                        <ProtectedRoute>
                          <BlogEditPostPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/onboarding"
                      element={
                        <ProtectedRoute>
                          <OnboardingModule />
                        </ProtectedRoute>
                      }
                    />

                    {/* ProsperityFIN module (Financial Analysis) */}
                    <Route
                      path="/fin"
                      element={
                        <ProtectedRoute allowedRoles={ADVISOR_LEVEL_ROLES}>
                          <FinLayout />
                        </ProtectedRoute>
                      }
                    >
                      <Route index element={<Navigate to="/fin/dashboard" replace />} />
                      <Route path="dashboard" element={<FinDashboard />} />
                      <Route path="clients" element={<FinPrivateRoute allowedRoles={[...ADVISOR_LEVEL_ROLES]}><FinClientManagement /></FinPrivateRoute>} />
                      <Route path="clients/:clientId" element={<FinPrivateRoute allowedRoles={[...ADVISOR_LEVEL_ROLES]}><FinClientDetails /></FinPrivateRoute>} />
                      <Route path="financial-analysis" element={<FinPrivateRoute allowedRoles={[...ADVISOR_LEVEL_ROLES]}><FinFinancialAnalysis /></FinPrivateRoute>} />
                      <Route path="financial-analysis/:clientId" element={<FinPrivateRoute allowedRoles={[...ADVISOR_LEVEL_ROLES]}><FinFinancialAnalysis /></FinPrivateRoute>} />
                      <Route path="clients/:clientId/report" element={<FinClientFinancialReport />} />
                      <Route path="proposals" element={<FinPrivateRoute allowedRoles={[...ADVISOR_LEVEL_ROLES]}><FinProposalManagement /></FinPrivateRoute>} />
                      <Route path="users" element={<FinPrivateRoute allowedRoles={['admin', 'manager']}><FinUserManagement /></FinPrivateRoute>} />
                      <Route path="client-portal" element={<FinPrivateRoute allowedRoles={['client']}><FinClientPortal /></FinPrivateRoute>} />
                      <Route path="client-financial-analysis" element={<FinPrivateRoute allowedRoles={['client']}><FinClientFinancialAnalysis /></FinPrivateRoute>} />
                      <Route path="profile-settings" element={<FinProfileSettings />} />
                      <Route path="projections-settings" element={<FinPrivateRoute allowedRoles={['admin']}><FinProjectionsSettings /></FinPrivateRoute>} />
                    </Route>

                    {/* Placeholder routes for future features */}
                    <Route
                      path="/dashboard/tools"
                      element={
                        <ProtectedRoute>
                          <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                            <div className="text-center">
                              <h2 className="text-2xl font-bold text-gray-900 mb-4">Professional Tools</h2>
                              <p className="text-gray-600 mb-4">Access your professional toolkit</p>
                              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 max-w-md mx-auto">
                                <p className="text-sm text-purple-800">Coming Soon - Professional tools and calculators</p>
                              </div>
                            </div>
                          </div>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/reports"
                      element={
                        <ProtectedRoute>
                          <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                            <div className="text-center">
                              <h2 className="text-2xl font-bold text-gray-900 mb-4">Reports & Analytics</h2>
                              <p className="text-gray-600 mb-4">View your performance metrics</p>
                              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 max-w-md mx-auto">
                                <p className="text-sm text-orange-800">Coming Soon - Performance reports and analytics</p>
                              </div>
                            </div>
                          </div>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/settings"
                      element={
                        <ProtectedRoute>
                          <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                            <div className="text-center">
                              <h2 className="text-2xl font-bold text-gray-900 mb-4">Account Settings</h2>
                              <p className="text-gray-600 mb-4">Manage your account preferences</p>
                              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 max-w-md mx-auto">
                                <p className="text-sm text-gray-700">Coming Soon - Account settings and preferences</p>
                              </div>
                            </div>
                          </div>
                        </ProtectedRoute>
                      }
                    />

                    {/* Root-level redirect handling */}
                    <Route path="/:slug" element={<RootRedirectHandler />}>
                      <Route index element={<AgentProfilePage />} />
                    </Route>

                    {/* Short link redirect handler */}
                    <Route path="/r/:slug" element={<RedirectHandler />} />

                    {/* Public Landing Pages */}
                    <Route path="/:username/:slug" element={<PublicLandingPage />} />
                    </Routes>
                  </div>
                </Suspense>
              </PendingLinksCountProvider>
            </Router>
          </RedirectsProvider>
        </LandingPagesProvider>
      </TeamsProvider>
    </AuthProvider>
  )
}

export default App
